import {AsyncMultiChoice, useTranslationContext} from 'platform/components';
import {VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always} from 'ramda';
import {isArray, isNotNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {useHttpCalls} from '../../api/useHttpCalls';
import {FILTER_VALUE_KEY} from '../../constants/filterValueKey';
import type {EagFilterProps} from '../../types/EagFilterProps';
import {getFilterFieldName} from '../../utils/getFilterFieldName';
import {getQuickFilterFromString} from '../../utils/quickFilterValue';
import {QuickFilterChips} from '../QuickFilterChips/QuickFilterChips';
import {useSuggestionCache} from './hooks/useSuggestionCache';

type SuggestionFilterProps = EagFilterProps & {
  suggestId: string;
  quickFilters?: Array<{value: string; label: string}>;
};

export function SuggestionFilter(props: SuggestionFilterProps) {
  const http = useHttpCalls();
  const t = useTranslationContext();

  const model = props.value[FILTER_VALUE_KEY] as string[] | string | null;

  const setSelectedIds = (values: string[] | string | null) => {
    props.onChange({[FILTER_VALUE_KEY]: values});
  };

  const {fetchOptions, fetchLabels} = useSuggestionCache({
    maxSize: 20,
    suggestId: props.suggestId,
    http,
  });

  const onChange = (values: string[] | null) => {
    const newSelectedIds = match(values)
      .when(isNotNilOrEmpty, (val) => val)
      .otherwise(always(null));

    setSelectedIds(newSelectedIds);
  };

  const isQuickFilterValue = getQuickFilterFromString(model);

  return (
    <VStack spacing={2}>
      <QuickFilterChips
        quickFilters={props.quickFilters}
        value={isQuickFilterValue}
        defaultValue={[]}
        setFilterValue={setSelectedIds}
        isDisabled={props.isDisabled}
        data-testid="quick-filters-suggestion-filter"
      />
      <AsyncMultiChoice<string>
        value={isArray(model) ? model : ([model].filter(Boolean) as string[])}
        onChange={onChange}
        loadOptions={fetchOptions}
        loadLabels={(values) => fetchLabels(values as string[])}
        defaultOptions
        noOptionsMessage={() => t('page.datagrid.labels.noSuggestFilterOptions')}
        placeholder={t('page.datagrid.filter.suggestPlaceholder', {
          field: getFilterFieldName(props.column),
        })}
        isDisabled={props.isDisabled}
        isNotClearable
        menuInPortal
        data-testid={suffixTestId('multi-choice-suggestion-filter', props)}
      />
    </VStack>
  );
}
